import React, { useEffect, useState } from "react";
import { Route, Routes } from "react-router-dom";

import { ChildThemeLayout } from "./Layout";
import { Provider } from "react-redux";
import { store } from "./_store";
import { config } from "./config";
import CustomLoader from "./components/CustomeLoader";

// Apply the tenant's theme (title, favicon, colour palette) from the config
// stored in localStorage. Called once config has loaded successfully.
const applyTheme = () => {
  const raw = localStorage.getItem("configData");
  const cfg = raw ? JSON.parse(raw) : null;

  document.title = cfg ? cfg.websiteName : "Sumo Ordering";

  const favicon = document.getElementById("favicon");
  if (cfg && favicon) {
    favicon.href = `${config.localApiUrl}/tenancy/assets/${cfg.favicon}`;
  }

  document.documentElement.style.setProperty("--teal-bg", cfg && cfg.colorPalatte);
  document.documentElement.style.setProperty("--text-teal", cfg && cfg.colorPalatte);
  document.documentElement.style.setProperty("--text-white", "#fff");
  document.documentElement.style.setProperty("--white-bg", "#fff");
  document.documentElement.style.setProperty("--text-black", "#000");
  document.documentElement.style.setProperty("--secondary-color", cfg && cfg.secondaryColor);
  document.documentElement.style.setProperty("--banner-bg", cfg && cfg.bannerColor);
  // top banner overlay opacity; unset keeps each theme's own default
  if (cfg && cfg.bannerOpacity) {
    document.documentElement.style.setProperty("--banner-opacity", cfg.bannerOpacity / 100);
  } else {
    document.documentElement.style.removeProperty("--banner-opacity");
  }

  const ogImage = document.createElement("meta");
  ogImage.setAttribute("property", "og:image:secure_url");
  document.head.appendChild(ogImage);
  ogImage.content = cfg && `${config.localApiUrl}/tenancy/assets/${cfg.favicon}`;
};

// Shown when the API reports the tenant site is disabled (HTTP 503), or when
// the server can't be reached and we have no cached config to fall back on.
const SiteUnavailable = ({ message }) => (
  <div
    style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#f4f4f5",
      fontFamily:
        "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif",
      color: "#27272a",
      padding: "16px",
    }}
  >
    <div
      style={{
        maxWidth: "480px",
        width: "100%",
        background: "#fff",
        borderRadius: "12px",
        padding: "40px 32px",
        textAlign: "center",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
      }}
    >
      <div style={{ fontSize: "44px", lineHeight: 1, marginBottom: "16px" }}>🚧</div>
      <h1 style={{ fontSize: "20px", margin: "0 0 12px", fontWeight: 600 }}>
        Site unavailable
      </h1>
      <p style={{ fontSize: "15px", lineHeight: 1.5, margin: 0, color: "#52525b" }}>
        {message || "This site is currently unavailable."}
      </p>
    </div>
  </div>
);

const ChildTheme = () => {
  // 'checking' → probing the API; 'ok' → render app; 'disabled' → blocked screen
  const [siteStatus, setSiteStatus] = useState("checking");
  const [disabledMessage, setDisabledMessage] = useState("");

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const res = await fetch(`${config.localApiUrl}/api/loadconfig`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        });

        // Super-admin has disabled this tenant's site.
        if (res.status === 503) {
          const body = await res.json().catch(() => ({}));
          if (!active) return;
          // Clear stale config so a later re-enable refetches cleanly.
          localStorage.removeItem("configData");
          setDisabledMessage(body.message || "This site is currently unavailable.");
          setSiteStatus("disabled");
          return;
        }

        const body = await res.json();
        if (body && body.data) {
          localStorage.setItem("configData", JSON.stringify(body.data));
        }
        if (!active) return;
        applyTheme();
        setSiteStatus("ok");
      } catch (e) {
        // Transient/network error: fall back to cached config if we have it,
        // otherwise surface a generic unavailable screen.
        if (!active) return;
        if (localStorage.getItem("configData")) {
          applyTheme();
          setSiteStatus("ok");
        } else {
          setDisabledMessage("Unable to reach the server. Please try again shortly.");
          setSiteStatus("disabled");
        }
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  if (siteStatus === "checking") {
    return <CustomLoader loading={true} />;
  }

  if (siteStatus === "disabled") {
    return <SiteUnavailable message={disabledMessage} />;
  }

  return (
    <Provider store={store}>
      <Routes>
        <Route path="/*" element={<ChildThemeLayout />} />
      </Routes>
    </Provider>
  );
};

export default ChildTheme;
